import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
} from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import AdminPage from '../../components/admin/AdminPage';
import LoadingCard from '../../components/LoadingCard';
import AdminUploadResource from '../../components/admin/AdminUploadResource';
import AdminUploadViewResource from '../../components/admin/AdminUploadViewResource';
import AdminResourceGallery from '../../components/admin/AdminResourceGallery';
import AdminViewGallery from '../../components/admin/AdminViewGallery';
import useMasterPlan from './hooks/useMasterPlan';

const ProjectMasterPlan = () => {
  const { id: projectId = '' } = useParams();
  const {
    project,
    catalog,
    expensesCatalog,
    operablePlan,
    setOperablePlan,
    updateExpenseValue,
    updateLongTermRentValue,
    onSaveOperablePlan,
    masterPlan,
    isNew,
    isLoading,
    rows,
    availableCatalog,
    pendingKey,
    setPendingKey,
    pendingValue,
    setPendingValue,
    addRow,
    removeRow,
    updateValue,
    onSubmit,
    handleUploadCover,
    handleUploadBlueprints,
    sweetAlert,
    alertProps,
    kindBuyerOptions,
    featuredOptions,
    loadingKindBuyer,
    loadingFeatured,
    selectedKindBuyer,
    setSelectedKindBuyer,
    selectedFeatured,
    setSelectedFeatured,
    onSaveIndicators,
    onDeleteBlueprint,
    onDeleteAllBlueprints,
  } = useMasterPlan({ projectId });

  const pageName = 'Configurar master plan';

  if (isLoading) {
    return <LoadingCard name={pageName} parent_name="Cargando..." />;
  }

  const getLabelForKey = (key: string) =>
    catalog!.find((c) => c.key === key)?.label ?? key;

  const getExpenseLabelForKey = (key: string) =>
    expensesCatalog?.find((c) => c.key === key)?.label ?? key;

  const coverGallery = [masterPlan?.cover].filter(Boolean) as string[];
  const blueprintGallery = (masterPlan?.blueprint_gallery ?? []).map((bp) => ({
    view: bp.label,
    image: bp.image,
  }));

  return (
    <AdminPage name={pageName} parentName={`${project!.code} - ${project!.name}`}>
      {sweetAlert && (
        <SweetAlert {...alertProps}>{alertProps.text || ''}</SweetAlert>
      )}

      {/* Características, Indicadores y Plan operable */}
      <Row>
        <Col xs={12} md={6}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <CardTitle tag="h4" className="mb-0 mr-2">
                  Características del master plan
                </CardTitle>
                <Badge color={isNew ? 'warning' : 'success'}>
                  {isNew ? 'Nuevo' : 'Editando'}
                </Badge>
              </div>

              {/* Added rows */}
              {rows.map((row) => (
                <FormGroup key={row.key} className="d-flex align-items-center">
                  <Label className="mb-0 mr-2" style={{ minWidth: 160 }}>
                    {getLabelForKey(row.key)}
                  </Label>
                  <Input
                    type="text"
                    value={row.value}
                    onChange={(e) => updateValue(row.key, e.target.value)}
                    className="mr-2"
                  />
                  <Button
                    color="danger"
                    size="sm"
                    type="button"
                    onClick={() => removeRow(row.key)}
                  >
                    <i className="ni ni-fat-remove" />
                  </Button>
                </FormGroup>
              ))}

              {/* Add new characteristic */}
              {availableCatalog.length > 0 && (
                <FormGroup className="d-flex align-items-center mt-3">
                  <Input
                    type="select"
                    value={pendingKey}
                    onChange={(e) => setPendingKey(e.target.value)}
                    className="mr-2"
                    style={{ maxWidth: 200 }}
                  >
                    <option value="">Seleccionar característica</option>
                    {availableCatalog.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </Input>
                  <Input
                    type="text"
                    value={pendingValue}
                    onChange={(e) => setPendingValue(e.target.value)}
                    placeholder="Valor"
                    className="mr-2"
                    disabled={!pendingKey}
                  />
                  <Button
                    color="secondary"
                    size="sm"
                    type="button"
                    disabled={!pendingKey}
                    onClick={addRow}
                  >
                    <i className="ni ni-fat-add mr-1" />
                    Agregar
                  </Button>
                </FormGroup>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button color="primary" type="button" onClick={onSubmit}>
                  Guardar características
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h4" className="mb-3">
                Indicadores del proyecto
              </CardTitle>

              <FormGroup>
                <Label className="form-control-label">
                  Tipo de comprador
                </Label>
                {!loadingKindBuyer && (
                  <Select
                    placeholder="Selecciona indicadores de tipo de comprador"
                    isMulti
                    options={kindBuyerOptions}
                    value={kindBuyerOptions.filter((opt: any) =>
                      selectedKindBuyer.includes(opt.value),
                    )}
                    onChange={(optionSelected: any) =>
                      setSelectedKindBuyer(
                        optionSelected.map((o: any) => o.value),
                      )
                    }
                  />
                )}
              </FormGroup>

              <FormGroup>
                <Label className="form-control-label">
                  Destacados
                </Label>
                {!loadingFeatured && (
                  <Select
                    placeholder="Selecciona indicadores destacados"
                    isMulti
                    options={featuredOptions}
                    value={featuredOptions.filter((opt: any) =>
                      selectedFeatured.includes(opt.value),
                    )}
                    onChange={(optionSelected: any) =>
                      setSelectedFeatured(
                        optionSelected.map((o: any) => o.value),
                      )
                    }
                  />
                )}
              </FormGroup>

              <div className="d-flex justify-content-end mt-3">
                <Button
                  color="primary"
                  type="button"
                  onClick={onSaveIndicators}
                  disabled={!masterPlan}
                >
                  Guardar indicadores
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-3">
                Configuración para calculadora de ROI
              </CardTitle>

              {/* Airbnb */}
              <h5 className="text-muted mb-3">Airbnb</h5>
              <FormGroup className="d-flex align-items-center">
                <Label className="mb-0 mr-2" style={{ minWidth: 200 }}>
                  Tasa de ocupación (%)
                </Label>
                <Input
                  type="number"
                  value={operablePlan.airbnb_occupancy_rate}
                  onChange={(e) =>
                    setOperablePlan((prev) => ({
                      ...prev,
                      airbnb_occupancy_rate: Number.parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </FormGroup>
              <FormGroup className="d-flex align-items-center">
                <Label className="mb-0 mr-2" style={{ minWidth: 200 }}>
                  Precio por noche
                </Label>
                <Input
                  type="number"
                  value={operablePlan.airbnb_price_per_night}
                  onChange={(e) =>
                    setOperablePlan((prev) => ({
                      ...prev,
                      airbnb_price_per_night: Number.parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </FormGroup>

              {/* Renta a largo plazo */}
              {operablePlan.long_term_rent.length > 0 && (
                <>
                  <h5 className="text-muted mb-3 mt-4">Renta a largo plazo</h5>
                  {operablePlan.long_term_rent.map((rent) => (
                    <FormGroup
                      key={rent.model_id}
                      className="d-flex align-items-center"
                    >
                      <Label className="mb-0 mr-2" style={{ minWidth: 200 }}>
                        {rent.model_name}
                      </Label>
                      <Input
                        type="number"
                        value={rent.monthly_rent}
                        onChange={(e) =>
                          updateLongTermRentValue(
                            rent.model_id,
                            Number.parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </FormGroup>
                  ))}
                </>
              )}

              {/* Gastos */}
              {operablePlan.expenses.length > 0 && (
                <>
                  <h5 className="text-muted mb-3 mt-4">Gastos</h5>
                  {operablePlan.expenses.map((row) => (
                    <FormGroup key={row.key} className="d-flex align-items-center">
                      <Label className="mb-0 mr-2" style={{ minWidth: 200 }}>
                        {getExpenseLabelForKey(row.key)}
                      </Label>
                      <Input
                        type="number"
                        value={row.value}
                        onChange={(e) =>
                          updateExpenseValue(row.key, Number.parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormGroup>
                  ))}
                </>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button
                  color="primary"
                  type="button"
                  disabled={!masterPlan}
                  onClick={onSaveOperablePlan}
                >
                  Guardar configuración
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Imágenes — only available once master plan exists */}

      {masterPlan?.id && (
        <>
          <Row className="mt-4">
            <Col xs={12} md={6}>
              <AdminUploadResource
                handleUpload={handleUploadCover}
                description="Subir portada"
                maxFiles={1}
              />
            </Col>
            <Col xs={12} md={6}>
              <AdminResourceGallery
                gallery={coverGallery}
                description="Portada actual"
              />
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={12} md={6}>
              <AdminUploadViewResource
                description="Subir planos"
                handleUpload={handleUploadBlueprints}
              />
            </Col>
            <Col xs={12} md={6}>
              <AdminViewGallery
                gallery={blueprintGallery}
                description="Planos actuales"
                onDelete={onDeleteBlueprint}
                onDeleteAll={onDeleteAllBlueprints}
              />
            </Col>
          </Row>
        </>
      )}
    </AdminPage>
  );
};

export default ProjectMasterPlan;
